import React from 'react';
import { Container, Typography, Box, Grid, Card, CardContent } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import FolderOpenRoundedIcon from '@mui/icons-material/FolderOpenRounded';
import SettingsSuggestRoundedIcon from '@mui/icons-material/SettingsSuggestRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import DatasetRoundedIcon from '@mui/icons-material/DatasetRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';

const Platform: React.FC = () => {
  const navigate = useNavigate();
  const platformFeatures = [
    {
      title: 'Request Management',
      description: 'Organize and manage your API requests with collections and folders.',
      icon: FolderOpenRoundedIcon,
    },
    {
      title: 'Environment Variables',
      description: 'Manage different environments with variables and configurations.',
      icon: SettingsSuggestRoundedIcon,
    },
    {
      title: 'Test Automation',
      description: 'Automate your API tests with test cycles and assertions.',
      icon: RocketLaunchRoundedIcon,
    },
    {
      title: 'Data Sets',
      description: 'Test with multiple data sets and validate responses.',
      icon: DatasetRoundedIcon,
    },
    {
      title: 'Swagger Import',
      description: 'Import your Swagger/OpenAPI specifications automatically.',
      icon: DescriptionRoundedIcon,
    },
    {
      title: 'History & Analytics',
      description: 'Track request history and analyze test results.',
      icon: QueryStatsRoundedIcon,
    },
  ];

  return (
    <Container maxWidth={false} disableGutters sx={{ py: 0 }}>
      <Box
        sx={{
          py: 2,
          px: { xs: 2, md: 4 },
          borderRadius: 0,
          minHeight: 'calc(100vh - 64px)',
          boxSizing: 'border-box',
          bgcolor: '#ffffff',
          border: '1px solid #e6edf8',
          boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
          '@keyframes cardFloat': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-5px)' },
          },
          '@keyframes titleGlow': {
            '0%, 100%': { textShadow: '0 0 0 rgba(30, 67, 177, 0)' },
            '50%': { textShadow: '0 0 12px rgba(30, 67, 177, 0.22)' },
          },
        }}
      >
        <Typography
          variant="h2"
          component="h1"
          align="center"
          sx={{
            color: '#0f172a',
            fontWeight: 700,
            animation: 'titleGlow 3s ease-in-out infinite',
            mb: 0.6,
          }}
        >
          Our Platform
        </Typography>
        <Typography
          variant="h6"
          align="center"
          sx={{ color: '#475569', mb: 1.2 }}
        >
          Built for modern API testing needs
        </Typography>

        <Grid container spacing={4} sx={{ mt: 1.5 }}>
          {platformFeatures.map((feature, index) => (
            <Grid key={feature.title} item xs={12} md={4}>
              {(() => {
                const Icon = feature.icon;
                return (
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: '1px solid #dbe7ff',
                  background: '#ffffff',
                  transition: 'transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
                  animation: 'cardFloat 4.6s ease-in-out infinite',
                  animationDelay: `${index * 180}ms`,
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    borderColor: '#1e43b1',
                    boxShadow: '0 16px 34px rgba(30, 67, 177, 0.16)',
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 2.2,
                    minHeight: 122,
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    pr: 12,
                  }}
                >
                  <Box
                    sx={{
                      width: 78,
                      height: 78,
                      borderRadius: '50%',
                      position: 'absolute',
                      top: '50%',
                      right: 12,
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: '#ffffff',
                      border: '1px solid rgba(30,67,177,0.14)',
                      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.08)',
                    }}
                  >
                    <Icon sx={{ color: '#0f2255', fontSize: 40 }} />
                  </Box>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: '#0f172a',
                      animation: 'titleGlow 3.4s ease-in-out infinite',
                      animationDelay: `${index * 140}ms`,
                      mb: 0.75,
                      fontSize: '1.1rem',
                      lineHeight: 1.2,
                    }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#475569',
                      lineHeight: 1.45,
                      transition: 'color 0.25s ease',
                      '.MuiCard-root:hover &': { color: '#334155' },
                      maxWidth: '70%',
                    }}
                  >
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
                );
              })()}
            </Grid>
          ))}
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signup')}
            sx={{
              bgcolor: '#0b3b9e',
              px: 4,
              borderRadius: 2,
              boxShadow: '0 8px 18px rgba(11,59,158,0.35)',
              '&:hover': {
                bgcolor: '#0d49bf',
              },
            }}
          >
            Try It Now
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default Platform;

